import { APP_BASE_HREF, DatePipe, DecimalPipe } from '@angular/common';
import {
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { CloseScrollStrategy, Overlay } from '@angular/cdk/overlay';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import { MSAL_INSTANCE, MsalService } from '@azure/msal-angular';
import { provideRouter, withDisabledInitialNavigation } from '@angular/router';

import { AppComponent } from './app/app.component';
import { AppConfigService } from './app/common/services/app-config.service/app-config.service';
import { AuthProviderFactory } from './app/common/factories/authentication/authentication-factory';
import { BrowserUtils } from '@azure/msal-browser';
import { ChatWidgetService } from './app/common/services/chat-widget/chat-widget.service';
import { FetchCMSContentService } from './app/common/services/fetch-cms-content/fetch-cms-content.service';
import { GlobalErrorHandler } from './app/common/services/global-error-handler/global-error-handler.service';
import { InactivityService } from './app/common/services/inactivity-service/inactivity.service';
import { LocaleService } from './app/common/services/locale-wrapper/locale.service';
import { LoginService } from './app/common/services/login-service/login.service';
import { NavigationHistoryService } from './app/common/services/navigation-history/navigation-history.service';
import { MAT_DATEPICKER_SCROLL_STRATEGY } from '@angular/material/datepicker';
import { MAT_SELECT_SCROLL_STRATEGY } from '@angular/material/select';
import { MSALInstanceFactory } from './app/common/msal/msal-instance-factory';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MonitoringService } from './app/common/services/app-monitoring-service/app-monitoring.service';
import { MsalInterceptor } from './app/common/msal/msal-interceptor';
import { RenderLabelPipe } from './app/common/pipes/render-label/render-label.pipe';
import { RequestInterceptor } from './app/common/interceptors/http-request/request-interceptor';
import { ThemeService } from './app/common/services/theme-service/theme.service';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { routes } from './app/routes';
import { provideAuth0 } from '@auth0/auth0-angular';
import { auth0StartupConfig } from './app/common/auth0/auth0.config';
import { CommonUtility } from './app/common/utils/commonUtils';
import { Auth0Interceptor } from './app/common/auth0/auth0.interceptor';
import { Auth0TokenService } from './app/common/services/auth0-service/auth0-service';
import { ApiService } from './app/common/services/api-service/api-service';
import { ProfileNotificationService } from './app/modules/detail/services/ProfileNotification.service';

/**
 * scroll factory
 * @param overlay overlay
 * @returns close scroll strategy
 */
export function scrollFactory(overlay: Overlay): () => CloseScrollStrategy {
  return (): CloseScrollStrategy =>
    overlay.scrollStrategies.close({ threshold: 100 });
}

const routerProvider =
  !BrowserUtils.isInIframe() && !BrowserUtils.isInPopup()
    ? provideRouter(routes) // Set to enabledBlocking to use Angular Universal
    : provideRouter(routes, withDisabledInitialNavigation());

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    provideAnimations(),
    routerProvider,
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideAppInitializer(() => {
      const initializerFn = AuthProviderFactory(
        inject(LoginService),
        inject(AppConfigService),
        inject(MonitoringService),
        inject(ThemeService),
        inject(InactivityService),
        inject(MsalService),
        inject(FetchCMSContentService),
        inject(LocaleService),
        inject(ChatWidgetService),
        inject(Auth0TokenService),
        inject(ApiService),
      );
      return initializerFn();
    }),
    provideAppInitializer(() => inject(NavigationHistoryService).init()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestInterceptor,
      multi: true,
    },
    // AC-12: Decide callback ownership before DI constructs the SDK AuthService.
    provideAuth0(auth0StartupConfig()),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [Auth0TokenService],
    },
    CommonUtility.isLocalhost()
      ? []
      : [
          // Sole Auth0 token injector; do not register the SDK AuthHttpInterceptor.
          {
            provide: HTTP_INTERCEPTORS,
            useClass: Auth0Interceptor,
            multi: true,
          },
          {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
          },
        ],
    MsalService,
    RenderLabelPipe,
    DatePipe,
    DecimalPipe,
    importProvidersFrom(
      MatDialogModule,
      MatSnackBarModule,
      BrowserAnimationsModule,
    ),
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: MAT_SELECT_SCROLL_STRATEGY,
      useFactory: scrollFactory,
      deps: [Overlay],
    },
    {
      provide: MAT_DATEPICKER_SCROLL_STRATEGY,
      useFactory: scrollFactory,
      deps: [Overlay],
    },
    {
      provide: APP_BASE_HREF,
      // useValue: `/${ROUTER_CONSTANTS.digitalPlatform}`,
      useFactory: (localeService: LocaleService): string => {
        /**
         * method to append locale whenever user hits URL without locale
         * example 1: old bookmarked Url when click should redirect to localized URL
         * example 2: Issue in Report URL when user try to download report from mail
         */
        localeService.reconcileLocaleOnHistoryTraversal();
        localeService.navigateToLocaleEnabledURL();
        // update base href only when env is non prod and user is not logged in
        return localeService.getBaseHrefWithLocale();
      },
      deps: [LocaleService],
    },
  ],
}).catch((err) => console.error(err));
